import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Badge,
  Icon,
  useColorModeValue,
  Center,
  Avatar,
  Spinner,
} from '@chakra-ui/react';
import { FiCalendar, FiTrendingUp, FiGift } from 'react-icons/fi';
import { supabase, fetchAllRows } from '../../../utils/supabase';
import { useVisibilityScope } from '../../../hooks/useVisibilityScope';
import { applyVisibilityScope, visibilityScopeKey } from '../../../utils/recordVisibility';
import moment from 'moment';

/**
 * Loan Anniversaries & Birthdays Widget
 * 
 * Displays loan anniversaries and contact birthdays within the next 7 days.
 * Anniversaries come from deals in any funded stage (loan-funded,
 * loan-brokercheck-received, loan-finalized) or the legacy `deal-won` slug;
 * birthdays come from crm_contacts.birthday (a DATE column).
 */
export default function LoanAnniversariesWidget({ userId, onContactClick }) {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const anniversaryBg = useColorModeValue('blue.50', 'blue.900');
  const birthdayBg = useColorModeValue('pink.50', 'pink.900');

  const [fundedDeals, setFundedDeals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Multi-owner visibility: the target user's effective book (owned + team
  // book-shares + record shares), per entity type.
  const { scope: dealScope } = useVisibilityScope({ entityType: 'deal', targetUserId: userId || null });
  const { scope: contactScope } = useVisibilityScope({ entityType: 'contact', targetUserId: userId || null });
  const dealScopeKey = visibilityScopeKey(dealScope) + ':g' + (dealScope?.grantsVersion || 0);
  const contactScopeKey = visibilityScopeKey(contactScope) + ':g' + (contactScope?.grantsVersion || 0);

  // Fetch funded deals (any funded variant or legacy deal-won) from Supabase
  useEffect(() => {
    if (!userId || !supabase) {
      setLoading(false);
      return;
    }

    const fetchFundedDeals = async () => {
      try {
        // fetchAllRows paginates past Supabase's 1000-row cap, so a long book
        // of funded loans can't silently truncate the anniversary list.
        const deals = await fetchAllRows(
          'crm_deals',
          'id, deal_name, contact_id, amount, estimated_closing_date',
          (query) => applyVisibilityScope(query, dealScope)
            .in('stage', ['loan-funded', 'loan-brokercheck-received', 'loan-finalized', 'deal-won'])
            .not('estimated_closing_date', 'is', null)
        );

        const formattedDeals = (deals || []).map(deal => ({
          id: deal.id,
          dealName: deal.deal_name,
          contactId: deal.contact_id,
          amount: deal.amount,
          estimatedClosingDate: deal.estimated_closing_date,
        }));
        setFundedDeals(formattedDeals);
      } catch (error) {
        console.error('Error fetching funded deals:', error);
        setFundedDeals([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFundedDeals();
    // dealScopeKey covers dealScope content (incl. grant versions)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, dealScopeKey]);

  // Fetch contacts with birthdays from Supabase
  useEffect(() => {
    if (!userId || !supabase) {
      setContacts([]);
      return;
    }

    const fetchContactsWithBirthdays = async () => {
      try {
        // Paginated for the same reason as funded deals: >1000 contacts with
        // birthdays must not silently drop rows.
        const contactsData = await fetchAllRows(
          'crm_contacts',
          'id, first_name, last_name, birthday',
          (query) => applyVisibilityScope(query, contactScope).not('birthday', 'is', null)
        );

        const formattedContacts = (contactsData || []).map(contact => ({
          id: contact.id,
          firstName: contact.first_name,
          lastName: contact.last_name,
          birthday: contact.birthday,
        }));
        setContacts(formattedContacts);
      } catch (error) {
        console.error('Error fetching contacts:', error);
        setContacts([]);
      }
    };

    fetchContactsWithBirthdays();
    // contactScopeKey covers contactScope content (incl. grant versions)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, contactScopeKey]);

  // Calculate upcoming events (anniversaries and birthdays within next 7 days)
  const upcomingEvents = useMemo(() => {
    const now = moment().startOf('day');
    const sevenDaysOut = moment().add(7, 'days').endOf('day');
    const events = [];

    // Add loan anniversaries from Supabase deals
    fundedDeals.forEach(deal => {
      if (!deal.estimatedClosingDate) return;
      // A DATE column arrives as 'YYYY-MM-DD'; moment parses it at local
      // midnight, so no timezone day-shift is possible here.
      const closeDate = moment(deal.estimatedClosingDate);
      if (!closeDate.isValid()) return;

      // A funded deal whose closing date is still in the future has not closed
      // yet — it must not surface as a fake "1yr" anniversary.
      if (closeDate.isAfter(now, 'day')) return;

      const nextAnniversary = closeDate.clone().year(now.year());

      // If anniversary already passed this year, check next year
      if (nextAnniversary.isBefore(now, 'day')) {
        nextAnniversary.add(1, 'year');
      }

      // Measure years at the occurrence, not at "now": a January close viewed
      // from the previous December would otherwise read one year short.
      const yearsAgo = nextAnniversary.year() - closeDate.year();

      // Deals closed earlier this year have no anniversary yet.
      if (yearsAgo < 1) return;

      if (nextAnniversary.isBetween(now, sevenDaysOut, 'day', '[]')) {
        events.push({
          type: 'anniversary',
          contactId: deal.contactId,
          dealId: deal.id,
          name: deal.dealName,
          date: nextAnniversary,
          yearsAgo,
          loanAmount: parseFloat(deal.amount) || 0,
        });
      }
    });

    // Add birthdays from contacts
    contacts.forEach(contact => {
      if (!contact.birthday) return;
      const birthDate = moment(contact.birthday);
      if (!birthDate.isValid()) return;

      const nextBirthday = birthDate.clone().year(now.year());

      // If birthday already passed this year, check next year
      if (nextBirthday.isBefore(now, 'day')) {
        nextBirthday.add(1, 'year');
      }

      if (nextBirthday.isBetween(now, sevenDaysOut, 'day', '[]')) {
        // Age they are turning on this birthday (occurrence year - birth year)
        const age = nextBirthday.year() - birthDate.year();

        events.push({
          type: 'birthday',
          contactId: contact.id,
          name: `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'Unknown',
          date: nextBirthday,
          age: age > 0 ? age : null,
        });
      }
    });

    // Sort by date (soonest first)
    return events.sort((a, b) => a.date.valueOf() - b.date.valueOf());
  }, [fundedDeals, contacts]);

  const getDaysUntil = (date) => {
    const days = date.diff(moment().startOf('day'), 'days');
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    return `In ${days} days`;
  };

  return (
    <Box
      bg={bgColor}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      p={2}
      shadow="sm"
      h="full"
      display="flex"
      flexDirection="column"
    >
      <HStack justify="space-between" mb={1.5}>
        <HStack spacing={1}>
          <Icon as={FiGift} boxSize={2.5} color="pink.500" />
          <Heading size="xs" fontSize="xs">Anniversaries & Birthdays</Heading>
        </HStack>
        <Badge colorScheme="pink" fontSize="3xs" px={1.5} py={0.5}>
          {upcomingEvents.length}
        </Badge>
      </HStack>

      <VStack align="stretch" spacing={0.5} flex={1} maxH="200px" overflowY="auto">
        {loading ? (
          <Center py={4}>
            <Spinner size="xs" />
          </Center>
        ) : upcomingEvents.length === 0 ? (
          <Center py={2} flexDirection="column">
            <Icon as={FiCalendar} boxSize={4} color="gray.300" mb={0.5} />
            <Text color="gray.500" fontSize="3xs">
              No upcoming events
            </Text>
            <Text color="gray.400" fontSize="3xs">
              (Next 7 days)
            </Text>
          </Center>
        ) : (
          upcomingEvents.map((event, index) => {
            const isAnniversary = event.type === 'anniversary';
            const eventBg = isAnniversary ? anniversaryBg : birthdayBg;
            const eventColor = isAnniversary ? 'blue' : 'pink';
            const daysUntil = getDaysUntil(event.date);
            const isToday = daysUntil === 'Today';
            
            return (
              <Box
                key={`${event.type}-${event.contactId}-${index}`}
                px={1.5}
                py={1}
                borderRadius="sm"
                borderWidth="1px"
                borderColor={isToday ? `${eventColor}.400` : borderColor}
                bg={isToday ? eventBg : 'transparent'}
                _hover={{ bg: hoverBg, cursor: 'pointer', transform: 'translateX(2px)' }}
                transition="all 0.2s"
                onClick={() => onContactClick && onContactClick(event.contactId)}
              >
                <HStack justify="space-between" align="center" spacing={1.5}>
                  <HStack spacing={1.5} flex={1} minW={0}>
                    <Icon 
                      as={isAnniversary ? FiTrendingUp : FiGift} 
                      boxSize={2.5}
                      color={`${eventColor}.500`}
                      flexShrink={0}
                    />
                    <VStack align="flex-start" spacing={0} flex={1} minW={0}>
                      <Text fontWeight="semibold" fontSize="3xs" noOfLines={1} lineHeight="1.2">
                        {event.name}
                      </Text>
                      <HStack spacing={1} flexWrap="nowrap">
                        <Badge 
                          colorScheme={eventColor} 
                          fontSize="3xs"
                          fontWeight="medium"
                          px={1}
                          py={0}
                        >
                          {isAnniversary ? (
                            `${event.yearsAgo}yr${event.yearsAgo > 1 ? 's' : ''}`
                          ) : (
                            event.age ? `${event.age}` : 'BD'
                          )}
                        </Badge>
                        {isAnniversary && event.loanAmount > 0 && (
                          <Text fontSize="3xs" color="gray.600" noOfLines={1} lineHeight="1.2">
                            ${(event.loanAmount / 1000).toFixed(0)}k
                          </Text>
                        )}
                      </HStack>
                    </VStack>
                  </HStack>
                  <Badge 
                    colorScheme={isToday ? 'green' : 'gray'} 
                    fontSize="3xs"
                    fontWeight={isToday ? 'bold' : 'normal'}
                    px={1}
                    py={0}
                    flexShrink={0}
                  >
                    {daysUntil === 'Today' ? 'Today' : daysUntil === 'Tomorrow' ? 'Tmrw' : event.date.format('M/D')}
                  </Badge>
                </HStack>
              </Box>
            );
          })
        )}
      </VStack>

      {upcomingEvents.length > 0 && (
        <HStack justify="center" mt={1} pt={1} borderTopWidth="1px" borderColor={borderColor}>
          <Text fontSize="3xs" color="gray.500">
            Next 7 days
          </Text>
        </HStack>
      )}
    </Box>
  );
}

